/**
 * PostHog analytics — React Native equivalent of web frontend posthog.ts
 * Uses posthog-react-native for mobile + web support.
 */
import PostHog from 'posthog-react-native'

const POSTHOG_KEY = process.env.EXPO_PUBLIC_POSTHOG_KEY || ''
const POSTHOG_HOST = process.env.EXPO_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com'

let client: PostHog | null = null

export function getPostHog(): PostHog | null {
  if (!POSTHOG_KEY) return null
  if (client) return client

  client = new PostHog(POSTHOG_KEY, {
    host: POSTHOG_HOST,
    enableSessionReplay: true,
  })

  return client
}

export function identifyUser(userId: string, properties?: Record<string, any>) {
  const ph = getPostHog()
  ph?.identify(userId, properties)
}

export function resetUser() {
  const ph = getPostHog()
  ph?.reset()
}

export function trackEvent(event: string, properties?: Record<string, any>) {
  const ph = getPostHog()
  ph?.capture(event, properties)
}

/**
 * Register A/B-bucket as super-property so ALL subsequent events are tagged.
 * Call once after bucket is assigned (or on app mount if bucket is already in storage).
 * Safe to call multiple times — posthog-react-native.register is idempotent.
 */
export function registerAbBucket(experiment: string, bucket: string) {
  const ph = getPostHog()
  ph?.register({ [`ab_${experiment}`]: bucket })
}

export function captureException(error: Error | unknown, context?: Record<string, any>) {
  const ph = getPostHog()
  if (!ph) return
  const err = error instanceof Error ? error : new Error(String(error))
  ph.capture('$exception', {
    $exception_message: err.message,
    $exception_type: err.name,
    $exception_stack_trace_raw: err.stack || '',
    ...context,
  })
}

/**
 * Initialize global error handlers to capture unhandled errors/rejections.
 * Reports to BOTH PostHog (analytics) and Sentry (error tracking).
 */
export function initGlobalErrorHandlers() {
  // Lazy import to avoid circular dependency (sentry.ts imports are resolved at call time)
  const getSentry = () => {
    try { return require('./sentry') } catch { return null }
  }

  if (typeof ErrorUtils !== 'undefined') {
    const originalHandler = ErrorUtils.getGlobalHandler()
    ErrorUtils.setGlobalHandler((error: Error, isFatal?: boolean) => {
      captureException(error, { source: 'global_error_handler', fatal: isFatal })
      getSentry()?.sentryCaptureException(error, { source: 'global_error_handler', fatal: isFatal })
      originalHandler?.(error, isFatal)
    })
  }

  // Unhandled promise rejections
  if (typeof global !== 'undefined') {
    const origRejection = (global as any).onunhandledrejection
    ;(global as any).onunhandledrejection = (event: any) => {
      const error = event?.reason instanceof Error ? event.reason : new Error(String(event?.reason || 'Unhandled rejection'))
      captureException(error, { source: 'unhandled_rejection' })
      getSentry()?.sentryCaptureException(error, { source: 'unhandled_rejection' })
      origRejection?.(event)
    }
  }
}

// Pre-defined events for consistency (matches web frontend)
export const events = {
  // Auth
  phoneEntered: (platform: string, uspCount?: number) =>
    trackEvent('phone_entered', { platform, ...(uspCount !== undefined ? { usp_count: uspCount } : {}) }),
  loginStarted: () => trackEvent('login_started'),
  loginCompleted: (method: 'mitid' | 'dev') => trackEvent('login_completed', { method }),
  logout: () => trackEvent('logout'),

  // What-matters (USP-selector pre-MitID) + A/B-bucket assignment
  abBucketAssigned: (experiment: string, bucket: 'what_matters' | 'direct') =>
    trackEvent('ab_bucket_assigned', { experiment, bucket }),
  whatMattersViewed: () => trackEvent('what_matters_viewed'),
  uspToggled: (usp: string, selected: boolean, total: number) =>
    trackEvent('usp_toggled', { usp, selected, total }),
  whatMattersContinued: (usps: string[]) =>
    trackEvent('what_matters_continued', { usps, usp_count: usps.length }),
  whatMattersSkipped: () => trackEvent('what_matters_skipped'),

  // Biometric enrollment
  biometricPrompted: (type: string) => trackEvent('biometric_prompted', { type }),
  biometricAccepted: (type: string) => trackEvent('biometric_accepted', { type }),
  biometricDeclined: (type: string, reason: 'user_skip' | 'os_deny' | 'error') =>
    trackEvent('biometric_declined', { type, reason }),

  // Onboarding
  onboardingStarted: () => trackEvent('onboarding_started'),
  onboardingStepCompleted: (step: number, stepName: string) =>
    trackEvent('onboarding_step_completed', { step, step_name: stepName }),
  onboardingCompleted: (categories: string[]) =>
    trackEvent('onboarding_completed', { categories }),

  // Quote requests
  quoteRequestCreated: (category: string) =>
    trackEvent('quote_request_created', { category }),
  quoteViewed: (providerId: string, providerName: string) =>
    trackEvent('quote_viewed', { provider_id: providerId, provider_name: providerName }),
  quoteAccepted: (providerId: string, providerName: string, category: string) =>
    trackEvent('quote_accepted', { provider_id: providerId, provider_name: providerName, category }),

  // Inbox
  inboxOpened: () => trackEvent('inbox_opened'),
  messageRead: (threadId: string) => trackEvent('message_read', { thread_id: threadId }),

  // Chat
  chatMessageSent: () => trackEvent('chat_message_sent'),
  chatSuggestionUsed: (suggestion: string) =>
    trackEvent('chat_suggestion_used', { suggestion }),

  // Settings
  settingsUpdated: (section: string) => trackEvent('settings_updated', { section }),
  fuldmagtSigned: () => trackEvent('fuldmagt_signed'),

  // Feedback
  feedbackSubmitted: (rating: number, trigger: string, pageUrl: string) =>
    trackEvent('feedback_submitted', { rating, trigger, page_url: pageUrl }),
  feedbackDismissed: (trigger: string) =>
    trackEvent('feedback_dismissed', { trigger }),

  // NPS
  npsSubmitted: (score: number, category: string) =>
    trackEvent('nps_submitted', { score, category }),
  npsDismissed: () =>
    trackEvent('nps_dismissed'),

  // Navigation
  screenViewed: (screen: string) => trackEvent('screen_viewed', { screen }),
}
